import type { Draft } from '@/api';
import type { ComposeSeed } from './ComposeDialog';

/**
 * Seeds the composer from a stored draft, identity included (ADR 0100).
 *
 * Every field the composer holds, plus the draft's `draftUuid` and
 * `updatedAt`, so the composer's first save is conditional on exactly the
 * version it was given. It lives here, not in the mail view, because the
 * composer itself needs it ("Load theirs", #180) and must not import the mail
 * view; `MailView`'s `seedFromDraft` is this function.
 */
export function draftSeed(draft: Draft): ComposeSeed {
  return {
    to: formatAddressList(draft.to),
    cc: formatAddressList(draft.cc),
    bcc: formatAddressList(draft.bcc),
    subject: draft.subject,
    bodyHtml: draft.bodyHtml,
    body: draft.bodyText ?? '',
    ...(draft.inReplyToMessageId ? { inReplyToMessageId: draft.inReplyToMessageId } : {}),
    ...(draft.forwardedMessageId ? { forwardedMessageId: draft.forwardedMessageId } : {}),
    mode:
      draft.mode === 'reply' || draft.mode === 'reply-all' || draft.mode === 'forward'
        ? draft.mode
        : 'new',
    draftId: draft.id,
    ...(draft.draftUuid ? { draftUuid: draft.draftUuid } : {}),
    draftUpdatedAt: draft.updatedAt,
    ...(draft.attachments ? { attachments: draft.attachments } : {}),
    ...(draft.fromAddress ? { from: draft.fromAddress } : {}),
  };
}

function formatAddressList(addresses: readonly { name?: string; email: string }[]): string {
  return addresses
    .map((address) => (address.name ? `${address.name} <${address.email}>` : address.email))
    .join(', ');
}
